/**
 * ChildClaimsTable — claims inherited from child TARAs.
 * Read-only data + Relevant checkbox + Justification input.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import { type ClaimRecord } from './claimsFieldSchema';
import styles from './Step7Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

export interface ChildClaimView extends ClaimRecord {}

export interface ChildClaimsTableProps {
  claims: ChildClaimView[];
  onToggleRelevance: (claimId: string) => void;
  onJustificationChange: (claimId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildClaimsTable({
  claims,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildClaimsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return claims;
    return claims.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [claims, search]);

  const totalPages = Math.max(1, Math.ceil(searched.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => searched.slice((page - 1) * pageSize, page * pageSize),
    [searched, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.claim_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'claim_id', label: 'ID', sortable: true, width: '7%' },
      { key: 'claim_statement', label: 'Claim Statement', sortable: false, width: '30%' },
      { key: 'justification', label: 'Justification', sortable: false, width: '24%' },
      {
        key: 'risk_ids',
        label: 'Linked Risks',
        sortable: false,
        width: '10%',
        render: (row: ChildClaimView) => joinList(row.risk_ids),
      },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '10%',
        render: (row: ChildClaimView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.claim_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.claim_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '19%',
        render: (row: ChildClaimView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.claim_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  return (
    <DataTableAny
      columns={columns}
      data={keyed}
      rowKey="_rowKey"
      emptyMessage="No inherited claims from child TARAs."
      toolbar={
        <TableToolbarAny
          search={{
            value: search,
            onChange: (v: string) => { setSearch(v); setPage(1); },
            placeholder: 'Search inherited claims…',
          }}
          count={{ value: searched.length, label: 'Inherited Claims' }}
          showFilterButton={false}
          actions={
            <RelevanceEditToggle
              editing={liveEditing}
              onToggle={handleToggle}
              pending={saving}
              disabled={disabled}
            />
          }
        />
      }
      pagination={
        <TablePaginationAny
          currentPage={page}
          totalItems={searched.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onItemsPerPageChange={(size: number) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      }
    />
  );
}
